import {
  AddShoppingCart,
  Delete,
  LocalTaxi,
  LocationCity,
  PersonSearch,
} from '@mui/icons-material';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import {
  DataGrid,
  GridActionsCellItem,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid';
import { api } from 'common';
import { citiesEqual, getEnabledCities } from 'common/sharedFunctions';
import { CircularLoading } from 'components';
import { Roles } from 'models';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { Android12Switch, ViewCarDetails, ViewUserDetails } from './Components';
import { DrawerContext } from 'context/DrawerContext/DrawerContext';
import { CarCreateForm } from 'forms';
import { CustomDateRange } from 'components';
import * as XLSX from 'xlsx';
import { editUser } from 'common/src/actions/usersactions';

// Ciudad por defecto para conductores sin `city` registrada. Comportamiento
// histórico (ver `fetchDrivers` en common): un conductor sin ciudad se cuenta
// como de Cúcuta.
const DEFAULT_CITY = 'Cúcuta';

// Valor centinela de la pestaña "Otras": conductores cuya ciudad no coincide con
// ninguna ciudad habilitada en /countries.
const CITY_OTHERS = '__others__';

// --- Configuración estática del DataGrid -------------------------------------
// Definida a nivel de módulo para que su identidad NO cambie entre renders: si
// `slots`, `slotProps` o `localeText` se recrean en cada render, el DataGrid
// remonta la toolbar (y pierde el foco del buscador) en cada tecleo/filtro.

const DriversQuickToolbar = () => (
  <Box
    sx={{
      p: 2,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    }}
  >
    <GridToolbarQuickFilter placeholder="Buscar..." />
  </Box>
);

const DATAGRID_SLOTS = { toolbar: DriversQuickToolbar };

const DATAGRID_SLOT_PROPS = {
  toolbar: {
    showQuickFilter: true,
    quickFilterProps: { placeholder: 'Buscar...' },
  },
};

const DATAGRID_INITIAL_STATE = {
  sorting: { sortModel: [{ field: 'createdAt', sort: 'desc' }] },
};

const DATAGRID_LOCALE_TEXT = {
  columnMenuLabel: 'Menú',
  columnMenuShowColumns: 'Mostrar columnas',
  columnMenuFilter: 'Filtrar',
  columnMenuHideColumn: 'Ocultar columna',
  columnMenuUnsort: 'Quitar orden',
  columnMenuSortAsc: 'Ordenar ascendente',
  columnMenuSortDesc: 'Ordenar descendente',

  filterPanelAddFilter: 'Añadir filtro',
  filterPanelDeleteIconLabel: 'Eliminar',
  filterPanelOperators: 'Operadores',
  filterPanelOperatorAnd: 'Y',
  filterPanelOperatorOr: 'O',
  filterPanelColumns: 'Columnas',
  filterPanelInputLabel: 'Valor',
  filterPanelInputPlaceholder: 'Valor para filtrar',

  filterOperatorContains: 'Contiene',
  filterOperatorEquals: 'Igual a',
  filterOperatorStartsWith: 'Empieza con',
  filterOperatorEndsWith: 'Termina en',
  filterOperatorIsEmpty: 'Está vacío',
  filterOperatorIsNotEmpty: 'No está vacío',
  filterOperatorIsAnyOf: 'Es alguno de',

  toolbarFilters: 'Filtros',
  toolbarFiltersLabel: 'Mostrar filtros',
  toolbarFiltersTooltipHide: 'Ocultar filtros',
  toolbarFiltersTooltipShow: 'Mostrar filtros',
  toolbarQuickFilterPlaceholder: 'Buscar…',
  toolbarQuickFilterLabel: 'Buscar',

  pagination: {
    labelDisplayedRows: '{from}-{to} de {count}',
    labelRowsPerPage: 'Filas por página:',
    firstButtonAriaLabel: 'Primera página',
    lastButtonAriaLabel: 'Última página',
    nextButtonAriaLabel: 'Página siguiente',
    previousButtonAriaLabel: 'Página anterior',
  },

  toolbarDensity: 'Densidad',
  toolbarDensityLabel: 'Densidad',
  toolbarDensityCompact: 'Compacta',
  toolbarDensityStandard: 'Estándar',
  toolbarDensityComfortable: 'Cómoda',

  errorOverlayDefaultLabel: 'Ocurrió un error',
  noRowsLabel: 'No hay filas',
  noResultsOverlayLabel: 'Sin resultados',

  toolbarExport: 'Exportar',
  toolbarExportLabel: 'Exportar',
  toolbarExportCSV: 'Descargar EXCEL',
  toolbarExportExcel: 'Descargar XLSX',
  toolbarExportPrint: 'Imprimir',

  checkboxSelectionHeaderName: 'Selección',
  checkboxSelectionSelectAllRows: 'Seleccionar todas las filas',
  checkboxSelectionUnselectAllRows: 'Deseleccionar todas las filas',

  columnGroupingHeaderName: 'Agrupación de columnas',
  rangeSelectionRowLabel: 'Filas seleccionadas',

  toolbarColumns: 'Columnas',
  toolbarColumnsLabel: 'Seleccionar columnas',
  toolbarColumnsTooltipShow: 'Mostrar columnas',
  toolbarColumnsTooltipHide: 'Ocultar columnas',
};

const DriverPage2 = () => {
  const auth = useSelector(state => state.auth);
  const staticusers = useSelector(state => state.usersdata.staticusers);
  const countriesdata = useSelector(state => state.countriesdata);
  const dispatch = useDispatch();
  const {
    fetchUsersOnce,
    deleteUser,
    updateUserApproval,
    updateUserServiceStatus,
    updateUserCar,
    updateCarAprroval,
    updateDriverActiveStatus,
    updateUserWalletBalance,
  } = api;
  const { handleOpenDrawer, updateContent } = useContext(DrawerContext);
  const [cityTab, setCityTab] = useState(
    auth.profile.city ? auth.profile.city : DEFAULT_CITY,
  );

  // Ciudades del filtro = ciudades habilitadas en /countries (o la lista estática
  // de respaldo si aún no se configuró ese nodo).
  const cityOptions = useMemo(
    () => getEnabledCities(countriesdata),
    [countriesdata],
  );

  // Sólo ADMIN/MANAGER eligen ciudad; un fleetadmin queda fijo a la suya.
  const canPickCity = [Roles.ADMIN, Roles.MANAGER].includes(
    auth.profile.usertype,
  );

  // Espejo de `cityOptions` para leerlo desde `columns` (memoizado con deps []).
  const cityOptionsRef = useRef(cityOptions);
  cityOptionsRef.current = cityOptions;

  // Valor que debe quedar marcado en el RadioGroup: la opción habilitada que
  // coincide con la ciudad seleccionada aunque difieran en acentos/mayúsculas
  // (pestaña "CUCUTA" ↔ perfil guardado como "Cúcuta").
  const activeCityValue = useMemo(() => {
    if (cityTab === CITY_OTHERS) return CITY_OTHERS;
    const match = cityOptions.find(c => citiesEqual(c, cityTab));
    return match || cityTab || '';
  }, [cityOptions, cityTab]);

  const handleDeleteUser = id => {
    Swal.fire({
      title: '¿Estás seguro?',
      text: '¡No podrás revertir esto!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    }).then(result => {
      if (result.isConfirmed) {
        dispatch(deleteUser(id));
        dispatch(fetchUsersOnce());
        Swal.fire('¡Eliminado!', 'El conductor ha sido eliminado.', 'success');
      }
    });
  };

  const updateApproval = async (id, approved) => {
    try {
      await updateUserApproval(id, approved);
      dispatch(fetchUsersOnce());
      Swal.fire(
        '¡Acción completa!',
        'Se ha actualizado la aprobación del conductor',
        'success',
        {
          timer: 2000,
        },
      );
    } catch (err) {
      console.error(err);
      Swal.fire('¡Error!', err.msg, 'error');
    }
  };

  const handleUserApproval = (id, approved) => {
    Swal.fire({
      title: `¿Estas seguro de ${
        !approved ? 'desaprobar' : 'aprobar'
      } este conductor?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: !approved ? 'Desaprobar' : 'Aprobar',
      cancelButtonText: 'Cancelar',
    }).then(result => {
      if (result.isConfirmed) {
        updateApproval(id, approved);
      }
    });
  };

  const updateService = async (id, queue) => {
    try {
      await updateUserServiceStatus(id, queue);
      dispatch(fetchUsersOnce());
      Swal.fire(
        '¡Acción completa!',
        `Se ha ${!queue ? 'desactivado' : 'activado'} el conductor`,
        'success',
        {
          timer: 2000,
        },
      );
    } catch (err) {
      console.error(err);
      Swal.fire('¡Error!', err.msg, 'error');
    }
  };

  const handleUserService = (id, queue) => {
    Swal.fire({
      title: `¿Estas seguro de ${
        !queue ? 'desactivar' : 'activar'
      } este conductor?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: !queue ? 'Desactivar' : 'Activar',
      cancelButtonText: 'Cancelar',
    }).then(result => {
      if (result.isConfirmed) {
        updateService(id, queue);
      }
    });
  };

  const updateCarApproval = async (userId, carId, approved) => {
    dispatch(
      updateUserCar(userId, {
        carApproved: !approved,
      }),
    );
    dispatch(updateCarAprroval(carId, !approved));
    dispatch(fetchUsersOnce());
  };

  const handleCarApproval = (row, approved) => {
    const { id: user, carId: id } = row;
    Swal.fire({
      title: `¿Estas seguro de ${
        !approved ? 'desaprobar' : 'aprobar'
      } este coche?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: !approved ? 'Desaprobar' : 'Aprobar',
      cancelButtonText: 'Cancelar',
    }).then(result => {
      if (result.isConfirmed) {
        updateCarApproval(user, id, approved ? false : true);
      }
    });
  };

  const updateDriverStatus = async (id, driverActiveStatus) => {
    try {
      await updateDriverActiveStatus(id, driverActiveStatus);
      dispatch(fetchUsersOnce());
      Swal.fire(
        '¡Acción completa!',
        `Se ha puesto en ${
          driverActiveStatus ? 'servicio' : 'descanso'
        } al conductor`,
        'success',
        {
          timer: 2000,
        },
      );
    } catch (err) {
      console.error(err);
      Swal.fire('¡Error!', err.msg, 'error');
    }
  };

  const handleDriverActiveStatus = async (id, driverActiveStatus) => {
    Swal.fire({
      title: `¿Estas seguro de cambiar al estado ${
        driverActiveStatus ? 'Descansando' : 'En servicio'
      } al conductor?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: driverActiveStatus ? 'Descansando' : 'En servicio',
      cancelButtonText: 'Cancelar',
    }).then(result => {
      if (result.isConfirmed) {
        updateDriverStatus(id, !driverActiveStatus);
      }
    });
  };

  const updateWalletBalance = async (id, walletBalance) => {
    try {
      await updateUserWalletBalance(id, walletBalance);
      dispatch(fetchUsersOnce());
      Swal.fire(
        '¡Acción completa!',
        `Se ha actualizado el saldo del conductor`,
        'success',
        {
          timer: 2000,
        },
      );
    } catch (err) {
      console.error(err);
      Swal.fire('¡Error!', err.msg, 'error');
    }
  };

  const handleWalletBalance = async (id, walletBalance) => {
    Swal.fire({
      title: `Modificar el saldo del conductor`,
      icon: 'warning',
      input: 'number',
      inputAttributes: {
        min: 0,
        step: 100,
        value: walletBalance,
      },
      inputValidator: value => {
        const numValue = parseFloat(value); // Convertir a número
        if (isNaN(numValue)) {
          return 'Debes ingresar un valor válido';
        }
        if (numValue < 0) {
          return 'El valor debe ser positivo';
        }
      },
      inputValue: walletBalance,
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
    }).then(result => {
      if (result.isConfirmed) {
        // Usamos el valor ingresado, no el valor original
        updateWalletBalance(id, parseFloat(result.value));
      }
    });
  };

  const columns = useMemo(
    () => [
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Acciones',
        width: 100,
        getActions: params => {
          let actions = [
            <ViewUserDetails
              label="Detalles del conductor"
              icon={<PersonSearch />}
              userId={params.id}
              closeMenuOnClick={false}
            />,
          ];

          if (!params.row.city) {
            actions.push(
              <GridActionsCellItem
                icon={<LocationCity />}
                label="Asignar ciudad"
                name="Asignar ciudad"
                onClick={() => {
                  try {
                    const inputOptions = (cityOptionsRef.current || []).reduce(
                      (acc, c) => ({ ...acc, [c]: c }),
                      {},
                    );
                    Swal.fire({
                      title: 'Asignar ciudad',
                      input: 'select',
                      inputOptions,
                      inputLabel: 'Ciudad',
                      inputPlaceholder: 'Selecciona la ciudad',
                      showCancelButton: true,
                      confirmButtonText: 'Asignar',
                      cancelButtonText: 'Cancelar',
                      inputValidator: value =>
                        !value && 'Selecciona una ciudad',
                    }).then(result => {
                      if (result.isConfirmed) {
                        dispatch(
                          updateUserCar(params.id, {
                            city: result.value,
                          }),
                        );
                        dispatch(editUser(params.id, { city: result.value }));
                        dispatch(fetchUsersOnce());
                      }
                    });
                  } catch (error) {
                    console.error(error);
                    Swal.fire(
                      '¡Error!',
                      'No se pudo asignar la ciudad',
                      'error',
                    );
                  }
                }}
              />,
            );
          }

          if (params.row.vehicleNumber) {
            actions.push(
              <ViewCarDetails
                label="Detalles del coche"
                icon={<LocalTaxi color="secondary" />}
                userId={params.row.id}
                closeMenuOnClick={false}
                props={params.row}
              />,
            );
          } else {
            actions.push(
              <GridActionsCellItem
                icon={<AddShoppingCart />}
                label="Asignar coche"
                name="Asignar coche"
                onClick={() => {
                  updateContent(<CarCreateForm userId={params.id} />);
                  handleOpenDrawer();
                }}
              />,
            );
          }

          return actions;
        },
      },
      {
        field: 'createdAt',
        type: 'Date',
        headerName: 'Creado',
        width: 100,
        valueGetter: (params, row) => {
          // Asegúrate de convertir createdAt a un objeto Date para la ordenación
          return row.createdAt ? new Date(row.createdAt) : null;
        },
        valueFormatter: (params, row) => {
          // Formatea la fecha para mostrarla en el formato deseado
          return row.createdAt
            ? new Date(row.createdAt).toLocaleDateString('en-GB') // Día/Mes/Año
            : '';
        },
      },
      {
        field: 'approved',
        type: 'boolean',
        headerName: 'Documentos aprobados',
        width: 100,
        renderCell: params => (
          <Android12Switch
            checked={params.row.approved}
            onChange={() => {
              handleUserApproval(params.id, !params.row.approved);
            }}
          />
        ),
      },
      {
        field: 'carApproved',
        type: 'string',
        headerName: 'Coche aprobado',
        width: 100,
        renderCell: params => (
          <Android12Switch
            checked={params.row.carApproved}
            onChange={() => {
              handleCarApproval(params.row, !params.row.carApproved);
            }}
          />
        ),
      },
      {
        field: 'driverActiveStatus',
        type: 'string',
        headerName: 'Estado',
        width: 150,
        renderCell: params => {
          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Android12Switch
                checked={params.row.driverActiveStatus}
                onChange={() => {
                  handleDriverActiveStatus(
                    params.row.id,
                    params.row.driverActiveStatus,
                  );
                }}
              />
              <Typography fontSize={12}>
                {params.row.driverActiveStatus ? 'En servicio' : 'Descansando'}
              </Typography>
            </div>
          );
        },
      },
      {
        field: 'firstName',
        type: 'string',
        headerName: 'Datos del conductor',
        width: 200,
        valueGetter: (value, row) => {
          return `${value} ${row.lastName}`;
        },
      },
      {
        field: 'email',
        type: 'string',
        headerName: 'Email',
        width: 200,
      },
      {
        field: 'mobile',
        type: 'string',
        headerName: 'Teléfono',
        width: 150,
      },
      {
        field: 'vehicleNumber',
        type: 'string',
        headerName: 'Placa',
        width: 70,
      },
      {
        field: 'walletBalance',
        type: 'numeric',
        headerName: 'Saldo',
        width: 100,
        renderCell: params => {
          return (
            <Button
              onClick={() => {
                handleWalletBalance(params.row.id, params.row.wallet);
              }}
            >
              ${params.row.walletBalance}
            </Button>
          );
        },
      },
      {
        field: 'queue',
        type: 'boolean',
        headerName: 'Ocupado',
        width: 70,
        renderCell: params => (
          <Android12Switch
            checked={params.row.queue}
            onChange={() => {
              handleUserService(params.id, !params.row.queue);
            }}
          />
        ),
      },

      {
        type: 'actions',
        width: 70,
        getActions: params => [
          <GridActionsCellItem
            icon={<Delete />}
            label="Delete"
            name="Eliminar conductor"
            title="Eliminar conductor"
            onClick={() => handleDeleteUser(params.id)}
            color="error"
          />,
        ],
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    dispatch(fetchUsersOnce());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Si la ciudad seleccionada no está entre las habilitadas (p. ej. el admin la
  // deshabilitó en /countries, o su perfil apunta a una ciudad que ya no opera),
  // caemos a la primera ciudad habilitada. La pestaña "Otras" queda excluida.
  useEffect(() => {
    if (!canPickCity || cityOptions.length === 0) return;
    if (cityTab === CITY_OTHERS) return;
    if (!cityOptions.some(c => citiesEqual(c, cityTab))) {
      setCityTab(cityOptions[0]);
    }
  }, [cityOptions, canPickCity, cityTab]);

  const rows = useMemo(() => {
    if (!auth || !auth.profile || !Array.isArray(staticusers)) return [];

    // Sólo roles con acceso a la vista de conductores.
    if (
      ![Roles.FLEET_ADMIN, Roles.ADMIN, Roles.MANAGER].includes(
        auth.profile.usertype,
      )
    ) {
      return [];
    }

    return staticusers.filter(user => {
      if (user.usertype !== Roles.DRIVER) return false;

      // Sin ciudad registrada ⇒ se cuenta como la ciudad por defecto (Cúcuta),
      // así estos conductores no desaparecen de la tabla.
      const effectiveCity =
        user.city && String(user.city).trim()
          ? String(user.city).trim()
          : DEFAULT_CITY;

      // "Otras": la ciudad del conductor no coincide con ninguna pestaña
      // habilitada (útil para detectar datos con typos o ciudades retiradas).
      if (cityTab === CITY_OTHERS) {
        return !cityOptions.some(c => citiesEqual(c, effectiveCity));
      }

      // Comparación tolerante a acentos/mayúsculas/espacios: la pestaña
      // "CUCUTA" hace match con conductores guardados como "Cúcuta"/" cúcuta ".
      return citiesEqual(effectiveCity, cityTab);
    });
  }, [auth, staticusers, cityTab, cityOptions]);

  const generateReportExcel = (fromDate, untilDate) => {
    console.log(rows.slice(0, 5));

    const data = rows.filter(row => {
      const created = new Date(row.createdAt);
      return (
        created.getTime() >= fromDate.getTime() &&
        created.getTime() <= untilDate.getTime()
      );
    });

    const dataToExport = data.map(row => {
      const rowData = {};
      columns.forEach(col => {
        rowData[col.headerName] = row[col.field];
      });
      return rowData;
    });

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Datos');

    XLSX.writeFile(workbook, 'exported_data.xlsx');
  };

  if (!Array.isArray(staticusers)) {
    return <CircularLoading />;
  }

  return (
    <div style={{ height: '70vh', width: '100%' }}>
      <Typography variant="h4" gutterBottom>
        Conductores
      </Typography>
      {canPickCity && (
        <FormControl>
          <FormLabel id="driver-city-filter-label">Ciudad</FormLabel>
          <RadioGroup
            row
            aria-labelledby="driver-city-filter-label"
            name="driver-city-filter"
            value={activeCityValue}
            onChange={e => setCityTab(e.target.value)}
          >
            {cityOptions.map(city => (
              <FormControlLabel
                key={city}
                value={city}
                control={<Radio />}
                label={city}
              />
            ))}
            <FormControlLabel
              value={CITY_OTHERS}
              control={<Radio />}
              label="Otras"
            />
          </RadioGroup>
        </FormControl>
      )}

      <CustomDateRange
        actionText={'Generar reporte'}
        onActionClick={generateReportExcel}
      />

      <div style={{ height: '90%', width: '100%' }}>
        <DataGrid
          columns={columns}
          rows={rows}
          slots={DATAGRID_SLOTS}
          slotProps={DATAGRID_SLOT_PROPS}
          localeText={DATAGRID_LOCALE_TEXT}
          initialState={DATAGRID_INITIAL_STATE}
        />
      </div>
    </div>
  );
};

export default DriverPage2;
